import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight, ChevronDown, Droplet, Droplets, Layers, Factory, Sprout, FileText, Home, Info, Mail, Newspaper, Package, Award } from "lucide-react";
import { NAV, COMPANY, IMAGES } from "../../lib/data";

const NAV_ICONS = {
  "/": Home,
  "/solutions": Package,
  "/industries": Factory,
  "/absorbents": Droplet,
  "/water-treatment": Droplets,
  "/ground-protection": Layers,
  "/premium-mats": Award,
  "/mat-catalog": Layers,
  "/land-stewardship": Sprout,
  "/resources": FileText,
  "/blog": Newspaper,
  "/about": Info,
  "/contact": Mail,
};

const slug = (s) => s.toLowerCase().replace(/\s+/g, "-");

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const linkClass = (active) =>
    `relative inline-flex items-center gap-1 px-1.5 py-2 text-[13px] font-medium whitespace-nowrap transition-colors ${
      active ? "text-flame" : scrolled ? "text-charcoal/80 hover:text-flame" : "text-white/90 hover:text-flame"
    }`;

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "glass-nav border-b border-border shadow-sm" : "bg-gradient-to-b from-ink/80 via-ink/40 to-transparent"
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-5 sm:px-8 flex items-center justify-between h-[72px]">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group shrink-0">
          <img
            src={scrolled ? IMAGES.markDark : IMAGES.markWhite}
            alt="OneHarvest"
            className="h-10 w-10 object-contain transition-opacity duration-300"
          />
          <div className="leading-none">
            <div className={`font-display font-extrabold text-lg tracking-tight transition-colors duration-300 ${scrolled ? "text-ink" : "text-white"}`}>ONEHARVEST</div>
          </div>
        </Link>

        <nav className="hidden 2xl:flex items-center gap-0.5">
          {NAV.map((n) => {
            const NavIcon = NAV_ICONS[n.to];
            if (n.children) {
              const active = location.pathname === n.to || n.children.some((c) => location.pathname === c.to);
              return (
                <div key={n.label} className="relative group">
                  <Link to={n.to || "#"} data-testid={`nav-link-${slug(n.label)}`} className={linkClass(active)}>
                    {NavIcon && <NavIcon className={`h-3.5 w-3.5 shrink-0 ${active ? "text-flame" : "text-flame/70"}`} />}
                    {n.label}
                    <ChevronDown className="h-3 w-3 opacity-70 transition-transform duration-200 group-hover:rotate-180" />
                    {active && <span aria-hidden="true" className="absolute bottom-0.5 left-1/2 -translate-x-1/2 h-0.5 w-4 bg-flame" />}
                  </Link>
                  <div className="absolute left-0 top-full pt-2 z-50 opacity-0 invisible translate-y-1 transition-all duration-200 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0">
                    <div className="min-w-[280px] bg-white border border-border shadow-xl py-2">
                      {n.children.map((c) => {
                        const CIcon = NAV_ICONS[c.to];
                        const cactive = location.pathname === c.to;
                        return (
                          <Link
                            key={c.to}
                            to={c.to}
                            data-testid={`nav-dropdown-${slug(c.label)}`}
                            className={`flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors ${cactive ? "text-flame bg-flame/5" : "text-charcoal/80 hover:text-flame hover:bg-muted/60"}`}
                          >
                            {CIcon && <CIcon className="h-4 w-4 shrink-0 text-flame/70" />}
                            {c.label}
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            }
            const active = location.pathname === n.to;
            return (
              <Link key={n.to} to={n.to} data-testid={`nav-link-${slug(n.label)}`} className={linkClass(active)}>
                {NavIcon && <NavIcon className={`h-3.5 w-3.5 shrink-0 ${active ? "text-flame" : "text-flame/70"}`} />}
                {n.label}
                {active && <span aria-hidden="true" className="absolute bottom-0.5 left-1/2 -translate-x-1/2 h-0.5 w-4 bg-flame" />}
              </Link>
            );
          })}
        </nav>

        <div className="hidden 2xl:block shrink-0">
          <Link
            to="/request-quote"
            data-testid="nav-request-quote"
            className="inline-flex items-center gap-2 bg-flame text-white px-5 py-2.5 text-sm font-semibold tracking-wide hover:bg-flame/90 transition-colors"
          >
            Request Quote <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen(!open)}
          className={`2xl:hidden p-2 transition-colors ${scrolled ? "text-ink" : "text-white"}`}
          aria-label="Menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="2xl:hidden glass-nav border-t border-border max-h-[80vh] overflow-auto">
          <div className="container-x py-4 flex flex-col">
            {NAV.map((n) => {
              const NavIcon = NAV_ICONS[n.to];
              if (n.children) {
                return (
                  <div key={n.label} className="border-b border-border">
                    <div className="py-3 font-semibold text-charcoal inline-flex items-center gap-2.5">
                      {NavIcon && <NavIcon className="h-4 w-4 shrink-0 text-flame/70" />}
                      {n.label}
                    </div>
                    <div className="pl-6 pb-2 flex flex-col">
                      {n.children.map((c) => {
                        const CIcon = NAV_ICONS[c.to];
                        const cactive = location.pathname === c.to;
                        return (
                          <Link
                            key={c.to}
                            to={c.to}
                            data-testid={`mobile-nav-${slug(c.label)}`}
                            className={`py-2.5 text-sm inline-flex items-center gap-2.5 ${cactive ? "text-flame" : "text-charcoal/80"}`}
                          >
                            {CIcon && <CIcon className="h-4 w-4 shrink-0 text-flame/70" />}
                            {c.label}
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                );
              }
              const active = location.pathname === n.to;
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={`mobile-nav-${slug(n.label)}`}
                  className={`py-3 border-b border-border font-medium inline-flex items-center gap-2.5 ${active ? "text-flame" : "text-charcoal"}`}
                >
                  {NavIcon && <NavIcon className={`h-4 w-4 shrink-0 ${active ? "text-flame" : "text-flame/70"}`} />}
                  {n.label}
                </Link>
              );
            })}
            <Link
              to="/request-quote"
              data-testid="mobile-request-quote"
              className="mt-4 bg-flame text-white text-center px-5 py-3 font-semibold"
            >
              Request Quote
            </Link>
            <a href={`tel:${COMPANY.phone}`} className="mt-2 text-center py-2 text-sm text-muted-foreground">
              {COMPANY.phone}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
